import { useState, useEffect } from "react";
import {
  adminNichesService,
  Niche,
  CreateNicheInput,
  UpdateNicheInput,
  NicheBrand,
  NicheAffiliate,
} from "../../services/admin/niches.admin.service";
import { DataTable } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Tag,
  AlertTriangle,
  CheckCircle,
  EyeOff,
  Building2,
  Users,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function AdminNiches() {
  const [niches, setNiches] = useState<Niche[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBrandsModalOpen, setIsBrandsModalOpen] = useState(false);
  const [isAffiliatesModalOpen, setIsAffiliatesModalOpen] = useState(false);
  const [selectedNiche, setSelectedNiche] = useState<Niche | null>(null);
  const [brands, setBrands] = useState<NicheBrand[]>([]);
  const [affiliates, setAffiliates] = useState<NicheAffiliate[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingAffiliates, setLoadingAffiliates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"all" | "enabled">("all");

  const [formData, setFormData] = useState<CreateNicheInput>({
    name: "",
    description: "",
  });

  const [editFormData, setEditFormData] = useState<UpdateNicheInput>({
    name: "",
    description: "",
    status: "ACTIVE",
  });

  useEffect(() => {
    fetchNiches();
  }, [statusFilter]);

  const fetchNiches = async () => {
    setLoading(true);
    try {
      const data = await adminNichesService.getAllNiches(statusFilter);
      setNiches(data);
    } catch (error: any) {
      console.error("Failed to fetch niches:", error);
      toast.error(error.response?.data?.message || "Failed to fetch niches");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({ name: "", description: "" });
    setIsCreateModalOpen(true);
  };

  const handleEdit = (niche: Niche) => {
    setSelectedNiche(niche);
    setEditFormData({
      name: niche.name,
      description: niche.description,
      status: niche.status,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (niche: Niche) => {
    setSelectedNiche(niche);
    setIsDeleteModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await adminNichesService.createNiche(formData);
      toast.success("Niche created successfully");
      setIsCreateModalOpen(false);
      setFormData({ name: "", description: "" });
      fetchNiches();
    } catch (error: any) {
      console.error("Failed to create niche:", error);
      toast.error(error.response?.data?.message || "Failed to create niche");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedNiche) return;

    if (!editFormData.name?.trim() || !editFormData.description?.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await adminNichesService.updateNiche(selectedNiche.id, editFormData);
      toast.success("Niche updated successfully");
      setIsEditModalOpen(false);
      setSelectedNiche(null);
      fetchNiches();
    } catch (error: any) {
      console.error("Failed to update niche:", error);
      toast.error(error.response?.data?.message || "Failed to update niche");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selectedNiche) return;

    setIsSubmitting(true);
    try {
      await adminNichesService.deleteNiche(selectedNiche.id);
      toast.success("Niche deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedNiche(null);
      fetchNiches();
    } catch (error: any) {
      console.error("Failed to delete niche:", error);
      toast.error(error.response?.data?.message || "Failed to delete niche");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (niche: Niche) => {
    const newStatus = niche.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await adminNichesService.updateNiche(niche.id, { status: newStatus });
      toast.success(
        `Niche ${newStatus === "ACTIVE" ? "activated" : "deactivated"} successfully`
      );
      fetchNiches();
    } catch (error: any) {
      console.error("Failed to update niche status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update niche status"
      );
    }
  };

  const handleViewBrands = async (niche: Niche) => {
    setSelectedNiche(niche);
    setLoadingBrands(true);
    setIsBrandsModalOpen(true);
    try {
      const data = await adminNichesService.getNicheBrands(niche.id);
      setBrands(data);
    } catch (error: any) {
      console.error("Failed to fetch brands:", error);
      toast.error(error.response?.data?.message || "Failed to fetch brands");
      setIsBrandsModalOpen(false);
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleViewAffiliates = async (niche: Niche) => {
    setSelectedNiche(niche);
    setLoadingAffiliates(true);
    setIsAffiliatesModalOpen(true);
    try {
      const data = await adminNichesService.getNicheAffiliates(niche.id);
      setAffiliates(data);
    } catch (error: any) {
      console.error("Failed to fetch affiliates:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch affiliates"
      );
      setIsAffiliatesModalOpen(false);
    } finally {
      setLoadingAffiliates(false);
    }
  };

  const columns: ColumnDef<Niche>[] = [
    {
      id: "name",
      header: "Name",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <div className="font-medium text-foreground">{row.original.name}</div>
      ),
    },
    {
      id: "description",
      header: "Description",
      cell: ({ row }) => (
        <div className="text-muted-foreground max-w-md truncate">
          {row.original.description}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-32" },
      cell: ({ row }) => {
        const niche = row.original;
        const statusConfig = {
          ACTIVE: {
            label: "Active",
            icon: CheckCircle,
            color: "text-green-600 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-500/10",
          },
          INACTIVE: {
            label: "Inactive",
            icon: EyeOff,
            color: "text-yellow-600 dark:text-yellow-400",
            bg: "bg-yellow-50 dark:bg-yellow-500/10",
          },
        };

        const config = statusConfig[niche.status];
        const Icon = config.icon;

        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}
          >
            <Icon size={14} />
            {config.label}
          </span>
        );
      },
    },
    {
      id: "brandCount",
      header: "Brands",
      meta: { width: "w-24" },
      cell: ({ row }) => {
        const niche = row.original;
        const count = niche.brandCount || 0;
        return (
          <button
            onClick={() => handleViewBrands(niche)}
            className={`text-sm font-medium ${
              count > 0
                ? "text-[#d7785b] hover:text-[#c5694f] hover:underline cursor-pointer"
                : "text-muted-foreground cursor-default"
            } transition-colors`}
            disabled={count === 0}
            title={count > 0 ? `View ${count} brand${count !== 1 ? "s" : ""}` : "No brands"}
          >
            <div className="flex items-center gap-1.5">
              <Building2 size={16} />
              <span>{count}</span>
            </div>
          </button>
        );
      },
    },
    {
      id: "affiliateCount",
      header: "Affiliates",
      meta: { width: "w-24" },
      cell: ({ row }) => {
        const niche = row.original;
        const count = niche.affiliateCount || 0;
        return (
          <button
            onClick={() => handleViewAffiliates(niche)}
            className={`text-sm font-medium ${
              count > 0
                ? "text-[#d7785b] hover:text-[#c5694f] hover:underline cursor-pointer"
                : "text-muted-foreground cursor-default"
            } transition-colors`}
            disabled={count === 0}
            title={count > 0 ? `View ${count} affiliate${count !== 1 ? "s" : ""}` : "No affiliates"}
          >
            <div className="flex items-center gap-1.5">
              <Users size={16} />
              <span>{count}</span>
            </div>
          </button>
        );
      },
    },
    {
      id: "createdAt",
      header: "Created At",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="text-muted-foreground">
          {format(new Date(row.original.createdAt), "MMM d, yyyy")}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { width: "w-28" },
      cell: ({ row: { original: niche } }) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(niche)}
            className="p-1.5 text-muted-foreground hover:text-[#d7785b] hover:bg-orange-50 dark:hover:bg-orange-500/10 rounded-lg transition-colors"
            title="Edit niche"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleToggleStatus(niche)}
            className="p-1.5 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
            title={
              niche.status === "ACTIVE"
                ? "Deactivate niche"
                : "Activate niche"
            }
          >
            {niche.status === "ACTIVE" ? (
              <EyeOff size={16} />
            ) : (
              <CheckCircle size={16} />
            )}
          </button>
          <button
            onClick={() => handleDelete(niche)}
            className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
            title="Delete niche"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-2">
        <div className="space-y-1">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            Niches Management
          </h1>
          <p className="text-muted-foreground text-lg">
            Manage niches for brands and affiliates
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select
            value={statusFilter}
            onValueChange={(value: "all" | "enabled") => setStatusFilter(value)}
          >
            <SelectTrigger className="w-[180px] border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Niches</SelectItem>
              <SelectItem value="enabled">Enabled Only</SelectItem>
            </SelectContent>
          </Select>

          <Button
            onClick={fetchNiches}
            variant="outline"
            className="border-border"
          >
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>

          <Button
            onClick={handleCreate}
            className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
          >
            <Plus size={16} className="mr-2" />
            Create Niche
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total Niches
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {niches.length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <Tag className="text-muted-foreground" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Active Niches
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {niches.filter((n) => n.status === "ACTIVE").length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-transparent border border-border rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Inactive Niches
              </p>
              <p className="text-3xl font-bold text-foreground mt-2">
                {niches.filter((n) => n.status === "INACTIVE").length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-2xl border border-border bg-transparent flex items-center justify-center">
              <EyeOff className="text-yellow-600 dark:text-yellow-400" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <DataTable
          data={niches}
          columns={columns}
          isLoading={loading}
          emptyText="No niches found"
        />
      </div>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader>Create New Niche</DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter niche name"
              />
            </div>
            <div>
              <Label htmlFor="create-description">Description</Label>
              <textarea
                id="create-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter niche description"
                rows={4}
                className="w-full rounded-md border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={isSubmitting}
              className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
            >
              {isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>Edit Niche</DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                placeholder="Enter niche name"
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    description: e.target.value,
                  })
                }
                placeholder="Enter niche description"
                rows={4}
                className="w-full rounded-md border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={editFormData.status}
                onValueChange={(value: "ACTIVE" | "INACTIVE") =>
                  setEditFormData({ ...editFormData, status: value })
                }
              >
                <SelectTrigger id="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={isSubmitting}
              className="bg-[#d7785b] hover:bg-[#c5694f] text-white"
            >
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle size={20} />
              Delete Niche
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium mb-2">
                ⚠️ Warning
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Deleting a niche will remove it from all users' profiles. It's
                better to update the niche name instead of deleting it, or
                deactivate it to prevent it from appearing for new joining
                brands and affiliates.
              </p>
            </div>
            <p className="text-muted-foreground">
              Are you sure you want to delete the niche{" "}
              <span className="font-semibold">
                "{selectedNiche?.name}"?
              </span>
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The niche will be permanently deleted
              from the database and removed from all users' profiles.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteSubmit}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brands Modal */}
      <Dialog open={isBrandsModalOpen} onOpenChange={setIsBrandsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Building2 size={20} />
              Brands in "{selectedNiche?.name}"
            </div>
          </DialogHeader>
          <div className="space-y-4">
            {loadingBrands ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="animate-spin text-muted-foreground" size={24} />
              </div>
            ) : brands.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No brands found in this niche
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  {brands.map((brand) => (
                    <div
                      key={brand.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{brand.name}</p>
                        <p className="text-sm text-muted-foreground">{brand.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBrandsModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Affiliates Modal */}
      <Dialog
        open={isAffiliatesModalOpen}
        onOpenChange={setIsAffiliatesModalOpen}
      >
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Users size={20} />
              Affiliates in "{selectedNiche?.name}"
            </div>
          </DialogHeader>
          <div className="space-y-4">
            {loadingAffiliates ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="animate-spin text-muted-foreground" size={24} />
              </div>
            ) : affiliates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No affiliates found in this niche
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <div className="space-y-2">
                  {affiliates.map((affiliate) => (
                    <div
                      key={affiliate.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-foreground">
                          {affiliate.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {affiliate.email}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAffiliatesModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

